import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useProducts } from '@/modules/products/hooks/useProducts';
import { usePlans } from '@/modules/plans/hooks/usePlans';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import type { SubscriptionStatus } from '@/modules/subscriptions/types';
import { EntitlementStatusBadge } from '@/modules/trials/components/EntitlementStatusBadge';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import { PaymentStatusBadge } from '@/modules/payments/components/PaymentStatusBadge';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { MonitorDaysBadge } from '../components/MonitorDaysBadge';
import { TrialStatusBadge } from '../components/TrialStatusBadge';
import { listAdminSubscriptionsMonitor } from '../services/subscriptionsMonitor.api';
import type {
  AdminSubscriptionMonitorItem,
  ListAdminSubscriptionsMonitorQuery,
} from '../types/adminSubscription';
import {
  ENTITLEMENT_STATUS_OPTIONS,
  SUBSCRIPTION_MONITOR_STATUSES,
} from '../types/adminSubscription';
import {
  EXPIRING_SOON_DAYS,
  computeMonitorKpis,
  formatDate,
  formatPaymentSummary,
} from '../utils/monitorHelpers';

function KpiSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5 animate-pulse">
      <div className="h-3 w-24 rounded bg-muted" />
      <div className="mt-3 h-8 w-16 rounded bg-muted" />
    </div>
  );
}

export function SubscriptionMonitorPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('');
  const [productFilter, setProductFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [entitlementFilter, setEntitlementFilter] = useState('');
  const [paymentIssueOnly, setPaymentIssueOnly] = useState(false);
  const [expiringSoonOnly, setExpiringSoonOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [user, isSuperAdmin, navigate]);

  const serverQuery = useMemo((): ListAdminSubscriptionsMonitorQuery => {
    const q: ListAdminSubscriptionsMonitorQuery = { limit: 100 };
    if (statusFilter) q.status = statusFilter;
    if (productFilter) q.product_id = productFilter;
    if (planFilter) q.plan_id = planFilter;
    if (entitlementFilter) q.entitlement_status = entitlementFilter;
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    if (expiringSoonOnly) q.expiring_soon_days = EXPIRING_SOON_DAYS;
    if (paymentIssueOnly) q.payment_issue = true;
    return q;
  }, [
    statusFilter,
    productFilter,
    planFilter,
    entitlementFilter,
    debouncedSearch,
    expiringSoonOnly,
    paymentIssueOnly,
  ]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'subscriptions', 'monitor', serverQuery],
    queryFn: () => listAdminSubscriptionsMonitor(serverQuery),
    enabled: isSuperAdmin,
  });

  const { data: productsData } = useProducts({ limit: 100 });
  const { data: plansData } = usePlans({ limit: 100 });

  const products = useMemo(() => productsData?.items ?? [], [productsData?.items]);
  const plans = useMemo(() => plansData?.items ?? [], [plansData?.items]);
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const kpis = useMemo(() => computeMonitorKpis(items), [items]);

  const applySearch = () => setDebouncedSearch(searchQuery);
  const unavailable = t('monitor.identity.unavailable');

  const formatPaymentStatus = useCallback(
    (status: string) => t(`payments.status.${status}`, { defaultValue: status }),
    [t],
  );

  const columns: ColumnDef<AdminSubscriptionMonitorItem>[] = useMemo(
    () => [
      {
        key: 'tenant',
        header: t('monitor.columns.tenant'),
        accessor: (row) => (
          <div className="space-y-0.5 min-w-0">
            <TrialTruncatedText
              value={row.tenant_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[14rem]"
              className="font-medium"
            />
            <TrialTruncatedText
              value={row.customer_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="text-xs text-muted-foreground"
            />
          </div>
        ),
        sortValue: (row) => row.tenant_name,
      },
      {
        key: 'customerEmail',
        header: t('monitor.columns.customerEmail'),
        accessor: (row) => (
          <TrialTruncatedText
            value={row.customer_email}
            fallback={unavailable}
            maxWidthClass="max-w-[11rem] lg:max-w-[15rem]"
          />
        ),
        sortValue: (row) => row.customer_email ?? '',
      },
      {
        key: 'product',
        header: t('monitor.columns.product'),
        accessor: (row) => (
          <TrialTruncatedText
            value={row.product_name}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (row) => row.product_name,
      },
      {
        key: 'plan',
        header: t('monitor.columns.plan'),
        accessor: (row) => (
          <TrialTruncatedText
            value={row.plan_name}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (row) => row.plan_name,
      },
      {
        key: 'subscriptionStatus',
        header: t('monitor.columns.subscriptionStatus'),
        accessor: (row) => <SubscriptionStatusBadge status={row.subscription_status} />,
        sortValue: (row) => row.subscription_status,
        align: 'center',
      },
      {
        key: 'trialStatus',
        header: t('monitor.columns.trialStatus'),
        accessor: (row) => (
          <TrialStatusBadge
            trialStatus={row.trial_status}
            subscriptionStatus={row.subscription_status}
          />
        ),
        sortValue: (row) => row.trial_status ?? '',
        align: 'center',
      },
      {
        key: 'startedAt',
        header: t('monitor.columns.startDate'),
        accessor: (row) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDate(row.started_at, locale)}
          </span>
        ),
        sortValue: (row) => row.started_at,
      },
      {
        key: 'periodEnd',
        header: t('monitor.columns.endDate'),
        accessor: (row) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDate(
              row.subscription_status === 'trialing' && row.trial_ends_at
                ? row.trial_ends_at
                : row.current_period_end,
              locale,
            )}
          </span>
        ),
        sortValue: (row) => row.current_period_end,
      },
      {
        key: 'daysRemaining',
        header: t('monitor.columns.daysRemaining'),
        accessor: (row) => (
          <div className="whitespace-nowrap flex justify-center">
            <MonitorDaysBadge item={row} />
          </div>
        ),
        sortValue: (row) => row.days_remaining ?? -1,
        align: 'center',
      },
      {
        key: 'payment',
        header: t('monitor.columns.payment'),
        accessor: (row) => {
          const summary = formatPaymentSummary(row, locale, formatPaymentStatus);
          if (!summary && !row.has_payment_issue) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <div className="space-y-1 min-w-0">
              {row.last_payment_status ? (
                <PaymentStatusBadge status={row.last_payment_status} />
              ) : null}
              {summary ? (
                <p className="text-xs text-muted-foreground truncate max-w-[12rem]" title={summary}>
                  {summary}
                </p>
              ) : null}
              {row.has_payment_issue ? (
                <span className="inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                  {t('monitor.paymentIssue')}
                </span>
              ) : null}
            </div>
          );
        },
        sortValue: (row) => row.last_payment_status ?? '',
        align: 'center',
      },
      {
        key: 'entitlement',
        header: t('monitor.columns.entitlement'),
        accessor: (row) => <EntitlementStatusBadge status={row.entitlement_status} />,
        sortValue: (row) => row.entitlement_status ?? '',
        align: 'center',
      },
      {
        key: 'actions',
        header: t('monitor.columns.actions'),
        accessor: (row) => (
          <div className="flex justify-end whitespace-nowrap shrink-0">
            <Link
              to="/dashboard/subscriptions/$subscriptionId"
              params={{ subscriptionId: row.subscription_id }}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10',
              )}
            >
              <Icons.Eye className="h-3.5 w-3.5" />
              {t('common.view')}
            </Link>
          </div>
        ),
        align: 'right',
      },
    ],
    [t, locale, unavailable, formatPaymentStatus],
  );

  if (!user || !isSuperAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('monitor.title')}
          subtitle={t('monitor.description')}
          icon={<Icons.BarChart className="h-7 w-7" />}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <KpiSkeleton key={i} />
          ))}
        </div>
        <LoadingPanel />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('monitor.title')}
          subtitle={t('monitor.description')}
          icon={<Icons.BarChart className="h-7 w-7" />}
        />
        <ErrorPanel message={t('monitor.error.loadFailed')} />
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => void refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHero
        title={t('monitor.title')}
        subtitle={t('monitor.description')}
        eyebrow={t('monitor.eyebrow')}
        icon={<Icons.BarChart className="h-7 w-7" />}
        metrics={[
          { label: t('monitor.kpi.active'), value: String(kpis.active) },
          { label: t('monitor.kpi.trialing'), value: String(kpis.trialing) },
          { label: t('monitor.kpi.paymentIssues'), value: String(kpis.paymentIssues) },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            isLoading={isFetching}
            aria-label={t('monitor.actions.refresh')}
          >
            <Icons.RefreshCw className="h-4 w-4" />
            {t('monitor.actions.refresh')}
          </Button>
        }
      />

      <p className="text-xs text-muted-foreground -mt-2">{t('monitor.kpi.loadedResultsNote')}</p>

      <KpiGrid className="gap-4 xl:grid-cols-3 2xl:grid-cols-6">
        <KpiCard
          title={t('monitor.kpi.active')}
          value={String(kpis.active)}
          tone="green"
          icon={<Icons.CheckCircle className="h-4 w-4" />}
        />
        <KpiCard
          title={t('monitor.kpi.trialing')}
          value={String(kpis.trialing)}
          tone="blue"
          icon={<Icons.Timer className="h-4 w-4" />}
        />
        <KpiCard
          title={t('monitor.kpi.expiredSuspended')}
          value={String(kpis.expiredOrSuspended)}
          tone="rose"
          icon={<Icons.XCircle className="h-4 w-4" />}
        />
        <KpiCard
          title={t('monitor.kpi.paymentIssues')}
          value={String(kpis.paymentIssues)}
          tone="amber"
          icon={<Icons.DollarSign className="h-4 w-4" />}
        />
        <KpiCard
          title={t('monitor.kpi.expiringSoon')}
          value={String(kpis.expiringSoon)}
          tone="violet"
          hint={t('monitor.kpi.expiringSoonHint', { days: EXPIRING_SOON_DAYS })}
          icon={<Icons.Timer className="h-4 w-4" />}
        />
        <KpiCard
          title={t('monitor.kpi.total')}
          value={String(kpis.total)}
          tone="cyan"
          icon={<Icons.Repeat className="h-4 w-4" />}
        />
      </KpiGrid>

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <label htmlFor="monitor-search" className="text-xs font-medium text-muted-foreground">
              {t('monitor.filters.search')}
            </label>
            <div className="flex gap-2">
              <Input
                id="monitor-search"
                placeholder={t('monitor.filters.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
              />
              <Button type="button" variant="secondary" size="sm" onClick={applySearch}>
                {t('monitor.filters.searchApply')}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="monitor-status" className="text-xs font-medium text-muted-foreground">
              {t('common.status')}
            </label>
            <select
              id="monitor-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SubscriptionStatus | '')}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('common.allStatuses')}</option>
              {SUBSCRIPTION_MONITOR_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`subscriptions.status.${s}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="monitor-product" className="text-xs font-medium text-muted-foreground">
              {t('monitor.filters.product')}
            </label>
            <select
              id="monitor-product"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('monitor.filters.allProducts')}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {i18n.language === 'ar' ? p.name_ar || p.name_en : p.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="monitor-plan" className="text-xs font-medium text-muted-foreground">
              {t('monitor.filters.plan')}
            </label>
            <select
              id="monitor-plan"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('monitor.filters.allPlans')}</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {i18n.language === 'ar' ? p.name_ar || p.name_en : p.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label
              htmlFor="monitor-entitlement"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('monitor.filters.entitlement')}
            </label>
            <select
              id="monitor-entitlement"
              value={entitlementFilter}
              onChange={(e) => setEntitlementFilter(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('monitor.filters.allEntitlements')}</option>
              {ENTITLEMENT_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`trials.entitlement.${s}`, s)}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none pb-2">
            <input
              type="checkbox"
              checked={expiringSoonOnly}
              onChange={(e) => setExpiringSoonOnly(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            {t('monitor.filters.expiringSoon')}
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none pb-2">
            <input
              type="checkbox"
              checked={paymentIssueOnly}
              onChange={(e) => setPaymentIssueOnly(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            {t('monitor.filters.paymentIssueOnly')}
          </label>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('monitor.filters.hint', { days: EXPIRING_SOON_DAYS })}
        </p>
      </div>

      {items.length === 0 ? (
        <EmptyPanel title={t('monitor.empty.title')} message={t('monitor.empty.message')} />
      ) : (
        <div className="hidden md:block min-w-0">
          <DataTable
            data={items}
            columns={columns}
            rowKey={(row) => row.subscription_id}
            total={data?.total ?? items.length}
            initialPageSize={10}
            emptyMessage={t('monitor.empty.title')}
          />
        </div>
      )}

      {data?.total !== undefined ? (
        <p className="text-xs text-muted-foreground">
          {t('monitor.footer.showing', {
            shown: items.length,
            total: data.total,
          })}
        </p>
      ) : null}
    </div>
  );
}
